const TOKEN_KEY = "duoyun.token";

export type UserRole = "admin" | "operator";

export interface SessionUser {
  id: string;
  username: string;
  role: UserRole;
}

export interface Session {
  user: SessionUser;
}

export interface LoginResponse {
  token: string;
  expiresIn: number;
}

export class UnauthorizedError extends Error {
  constructor(message = "未登录或登录已过期") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends Error {
  constructor(message = "无权执行此操作") {
    super(message);
    this.name = "ForbiddenError";
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function handle(res: Response) {
  if (res.status === 401) {
    clearToken();
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new UnauthorizedError();
  }

  const text = await res.text().catch(() => "");
  let message = text || `HTTP ${res.status}`;
  try {
    const body = JSON.parse(text);
    message = body.error || message;
  } catch {
    // Non-JSON error responses use their response text.
  }

  if (res.status === 403) {
    throw new ForbiddenError(message);
  }
  if (!res.ok) {
    throw new Error(message);
  }
  if (res.status === 204) return null;
  return text ? JSON.parse(text) : null;
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch("/api/v1" + path, { headers: authHeaders() });
  return handle(res) as Promise<T>;
}

export async function apiSend<T = unknown>(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const opts: RequestInit = { method, headers: authHeaders() };
  if (body instanceof FormData) {
    opts.body = body;
  } else if (body !== undefined) {
    (opts.headers as Record<string, string>)["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch("/api/v1" + path, opts);
  return handle(res) as Promise<T>;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: "Bearer " + token } : {};
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return handle(res) as Promise<LoginResponse>;
}

export async function getSession(): Promise<Session> {
  return apiGet<Session>("/auth/session");
}
